<template>
    <div class="qr-issue-page">
        <!-- Header -->
        <div class="issue-header">
            <h3 class="issue-header-title">Pengiriman QR</h3>

            <div class="issue-header-actions">
                <div class="issue-saldo">Saldo QR : {{ formatCount(saldoQr) }}</div>

                <!-- Date range picker -->
                <div class="date-picker-wrapper">
                    <i class="fas fa-calendar-alt calendar-icon"></i>
                    <flat-pickr v-model="dateValue" :config="dateRangeConfig"></flat-pickr>
                </div>

                <a class="btn-download-template" href="/template/Data pengiriman Sticker QR Code ke Dealer atau AHASS.xlsx">
                    <i class="fas fa-file-download"></i>
                    <span>Download Template</span>
                </a>
            </div>
        </div>

        <!-- Kartu Dealer -> POS (jumlah pengiriman) -->
        <div class="kpi-row">
            <div class="kpi-card" v-for="card in posCards" :key="card.key">
                <div class="kpi-label-row">
                    <span class="kpi-label">{{ card.title }}</span>
                    <a
                        v-if="card.status"
                        href="#"
                        class="kpi-link"
                        @click.prevent="openDrawer(card.status)"
                    >
                        Lihat detail →
                    </a>
                </div>
                <div class="kpi-value">{{ card.value }}</div>
                <div class="kpi-unit">pengiriman</div>
            </div>
        </div>

        <!-- Ringkasan Pengiriman -->
        <div class="group-row">
            <div class="group-card" v-for="block in summaryBlocks" :key="block.key">
                <div class="group-title-row">
                    <span class="group-title">Pengiriman</span>
                    <span class="route-badge">
                        <span>{{ block.from }}</span>
                        <svg class="route-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M2.5 7h9M8 3.5 11.5 7 8 10.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <span>{{ block.to }}</span>
                    </span>
                </div>
                <div class="group-tiles">
                    <div class="group-tile" v-for="row in block.rows" :key="row.key">
                        <div class="group-tile-label">{{ row.label }}</div>
                        <div class="group-tile-value">{{ row.value }}</div>
                    </div>
                </div>
            </div>
        </div>

        <qr-issue
            class="qr-issue-history"
            ref="qrIssue"
            :start-date="startDate"
            :end-date="endDate"
            @editData="handleEditData"
            @previewBast="handlePreviewBast"
            @showMetodeModal="modal.showMetodeModal = true"
        />

        <edit-pengiriman-modal
            ref="editModal"
            v-if="modal.showEditModal"
            @closeModal="modal.showEditModal = false"
            @updateTable="updateTable"
        />

        <metode-pengiriman-modal
            v-if="modal.showMetodeModal"
            @closeModal="modal.showMetodeModal = false"
            @updateTable="updateTable"
        />

        <bast-preview-modal
            v-if="bastPreview"
            :issue-id="bastPreview.issueId"
            :bast-no="bastPreview.bastNo"
            :qty="bastPreview.qty"
            @close="bastPreview = null"
        />

        <pos-shipment-drawer
            v-if="drawerStatus"
            :status="drawerStatus"
            :start-date="startDate"
            :end-date="endDate"
            @close="drawerStatus = null"
        />
    </div>
</template>

<script>
import moment from 'moment'
import qrIssue from './children/qrIssue.vue'
import BastPreviewModal from './children/BastPreviewModal.vue'
import PosShipmentDrawer from './children/PosShipmentDrawer.vue'

import EditPengirimanModal from './children/EditPengirimanModal.vue';
import MetodePengirimanModal from './children/MetodePengirimanModal.vue';
import { fetchIssueHighlight, withTotal, formatCount } from '@/services/qr-issue'

const RANGE_SEPARATOR = ' s/d '

export default {
    metaInfo : {
        title : 'QR Issue'
    },
    components : {
        MetodePengirimanModal,
        EditPengirimanModal,
        BastPreviewModal,
        PosShipmentDrawer,
        qrIssue
    },
    data () {
        // default periode = bulan berjalan
        const start = moment().startOf('month').format('YYYY-MM-DD')
        const end = moment().format('YYYY-MM-DD')
        return {
            modal : {
                showMetodeModal : false,
                showEditModal: false
            },
            dataEdit : {
                id : '',
                package_code: '',
                kode_dealer : '',
                qty : 0,
                sender_signature : null,
            },
            saldoQr : 0,

            // tanggal (dikontrol parent, dikirim ke child lewat props)
            startDate: start,
            endDate: end,
            dateValue: `${start}${RANGE_SEPARATOR}${end}`,
            dateRangeConfig: {
                mode: 'range',
                altFormat: 'd-m-Y',
                dateFormat: 'Y-m-d',
                altInput: true,
                altInputClass: 'qr-flatpickr-alt',
                locale: { rangeSeparator: RANGE_SEPARATOR },
                onClose: this.onCloseDate
            },

            // posShipment = jumlah pengiriman Dealer -> POS, dealerQr = QR MD -> Dealer,
            // posQr = QR Dealer -> POS (null jika gagal dimuat atau tidak dikirim backend)
            highlight : {
                posShipment: null,
                dealerQr: null,
                posQr: null,
            },
            highlightRequestId: 0,

            // state modal preview BAST & drawer POS
            bastPreview: null,
            drawerStatus: null,
        }
    },
    computed : {
        highlightParams () {
            return {
                startDate: this.startDate,
                endDate: this.endDate,
            }
        },

        highlightKey () {
            return JSON.stringify(this.highlightParams)
        },

        posCards () {
            const cards = withTotal(this.highlight.posShipment)
            const show = (value) => cards ? formatCount(value) : '-'
            return [
                { key: 'total', title: 'Total Kirim ke POS', value: show(cards && cards.total) },
                { key: 'intransit', title: 'Intransit POS', value: show(cards && cards.intransit), status: 'INTRANSIT' },
                { key: 'received', title: 'Diterima POS', value: show(cards && cards.received), status: 'RECEIVED' },
            ]
        },

        summaryBlocks () {
            const { dealerQr, posQr } = this.highlight
            return [
                this.buildSummaryBlock('md', 'MD', 'Dealer', dealerQr),
                this.buildSummaryBlock('pos', 'Dealer', 'POS', posQr),
            ]
        },
    },
    mounted() {
        this.fetchData();
        this.fetchHighlight();

    },
    beforeMount () {
        document.getElementById('content').style.background = '#F8F8F8';
    },
    beforeDestroy () {
        document.getElementById('content').style.background = '';
    },
    methods : {
        formatCount,
        onCloseDate (dates) {
            if (!dates.length) return
            const fmt = (d) => moment(d).format('YYYY-MM-DD')
            this.startDate = fmt(dates[0])
            this.endDate = fmt(dates[1] || dates[0])
        },
        openDrawer (status) {
            this.drawerStatus = status
        },
        handlePreviewBast (row) {
            this.bastPreview = { issueId: row.id, bastNo: row.bastNo, qty: row.qty }
        },
        // Total QR Dikirim = Dalam Perjalanan + Sudah Diterima (tidak memakai field sent)
        buildSummaryBlock (key, from, to, qr) {
            const stat = withTotal(qr)
            const show = (value) => stat ? formatCount(value) : '-'
            const intransit = stat ? stat.intransit : 0
            const received = stat ? stat.received : 0
            return {
                key,
                from,
                to,
                rows: [
                    { key: 'sent', label: 'Total QR Dikirim', value: show(stat && stat.total) },
                    { key: 'intransit', label: 'QR Dalam Perjalanan', value: show(intransit) },
                    { key: 'received', label: 'QR Sudah Diterima', value: show(received) },
                ],
            }
        },
        showMetodeModal () {
            this.modal.showMetodeModal = true
        },
        handleEditData (data) {
            this.modal.showEditModal = true

            this.dataEdit.id = data.id
            this.dataEdit.package_code = data.package_code || data.package || data.code || ''
            this.dataEdit.kode_dealer = data.kode_dealer
            this.dataEdit.qty = data.qty
            this.dataEdit.sender_signature = data.sender_signature
        },

        fetchHighlight () {
            // abaikan respons lama jika ada request yang lebih baru
            const requestId = ++this.highlightRequestId
            fetchIssueHighlight(this.$http, this.highlightParams)
            .then((highlight) => {
                if (requestId !== this.highlightRequestId) return
                this.highlight = highlight
            })
            .catch((err) => {
                console.log(err)
                if (requestId !== this.highlightRequestId) return
                this.highlight = { posShipment: null, dealerQr: null, posQr: null }
            })
        },

        updateTable () {
            this.$refs.qrIssue.generateDatas();
            this.fetchData()
            this.fetchHighlight()
            this.modal.showMetodeModal = false;
            this.modal.showEditModal = false;
        },
        fetchData() {
            if (this.$store.state.userData.userData.role !== 'USER SE') {
                this.$http.get(`/api/qrs/highlight`)
                .then((resp)=>{
                    this.saldoQr = resp.data.data.qty_after
                })
                .catch((err)=>{console.log(err)})
            } else {
                this.$http.get(`/api/qrs/highlight/dealer?start_date=${moment().format('YYYY-MM-DD')}&end_date=${moment().format('YYYY-MM-DD')}`)
                .then((resp)=>{
                    this.saldoQr = resp.data.data.qty_after
                })
                .catch((err)=>{console.log(err)})
            }
        },
    },
    watch : {
        // refetch hanya kalau tanggal berubah
        highlightKey () {
            this.fetchHighlight()
        }
    },
}
</script>

<style>
    .mpm-button {
        background-color :#F36F21 !important;
        color : white;
        height: 47px;
    }

    .mpm-button-outline {
        background-color :white !important;
        color : #F36F21;
        height: 47px;
    }

    .h-47px {
        height: 47px;
    }

    /* flat-pickr renders its own alt input, so this cannot be scoped. */
    .qr-issue-page .qr-flatpickr-alt {
        border: none !important;
        outline: none !important;
        font-size: 12px !important;
        background: transparent !important;
        color: #2F2F2F !important;
        width: 162px !important;
        height: 30px !important;
        padding: 0 !important;
        cursor: pointer !important;
    }
</style>

<style scoped>
    /* Page header */
    .issue-header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px 16px;
        margin-bottom: 24px;
    }

    .issue-header-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #2F2F2F;
    }

    .issue-header-actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
        gap: 12px 16px;
    }

    .issue-saldo {
        font-size: 12px;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .date-picker-wrapper {
        display: flex;
        align-items: center;
        width: 213px;
        height: 32px;
        padding: 0 16px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
    }

    .calendar-icon {
        margin-right: 9px;
        color: #2F2F2F;
        font-size: 14px;
    }

    .btn-download-template {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        height: 32px;
        padding: 0 24px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
        font-size: 12px;
        color: #2F2F2F;
        white-space: nowrap;
        cursor: pointer;
    }

    .btn-download-template:hover {
        background-color: #FAFAFA;
        color: #2F2F2F;
        text-decoration: none;
    }

    /* KPI cards (Dealer -> POS) */
    .kpi-row,
    .group-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-bottom: 16px;
    }

    .kpi-card {
        flex: 1 1 240px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 20px;
        background-color: #FFFFFF;
        border-radius: 8px;
    }

    .kpi-label-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        min-height: 23px;
        font-size: 12px;
    }

    .kpi-label {
        color: #4B4B4B;
    }

    .kpi-link {
        flex-shrink: 0;
        font-weight: 600;
        color: #F36F21;
        white-space: nowrap;
        cursor: pointer;
    }

    .kpi-link:hover {
        color: #F36F21;
        text-decoration: underline;
    }

    .kpi-value {
        font-size: 24px;
        font-weight: 600;
        line-height: 1.2;
        color: #2F2F2F;
    }

    .kpi-unit {
        font-size: 14px;
        color: #111111;
    }

    /* Ringkasan groups */
    .group-row {
        margin-bottom: 24px;
    }

    .group-card {
        flex: 1 1 420px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 16px;
        background-color: #FFFFFF;
        border-radius: 8px;
    }

    .group-title-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .group-title {
        font-size: 14px;
        font-weight: 600;
        color: #2F2F2F;
    }

    .route-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        background-color: #F2F2F2;
        border-radius: 999px;
        font-size: 12px;
        line-height: 1.2;
        color: #4B4B4B;
        white-space: nowrap;
    }

    .route-arrow {
        flex-shrink: 0;
    }

    .group-tiles {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    }

    .group-tile {
        flex: 1 1 130px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 20px;
        background-color: #F8F8F8;
        border-radius: 8px;
    }

    .group-tile-label {
        min-height: 23px;
        font-size: 12px;
        color: #4B4B4B;
    }

    .group-tile-value {
        font-size: 24px;
        font-weight: 600;
        line-height: 1.2;
        color: #2F2F2F;
    }
</style>
